import {
  ActivityCard,
  Select,
  FormLabel,
  Heading,
  Loader,
} from "@/components";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from "@/components/ui";
import useActivityDashboard from "./useActivityDashboard";
import { yearOptions } from "@/lib/utils";
import { CostAnalytics } from "./CostAnalytics";
import { AUDIT_TRAIL, BILLING, CLEARING, COLLECTION, GL, INVOICE, PAYMENT } from "./consts";

const ActivityDashboard = () => {
  const {
    communityList,
    isCommunityLoading,
    onCommunityChange,
    communityId,
    communityData,
    year,
    onYearChange,
    invoiceCards,
    paymentCards,
    onAccordionChange,
    openTabs,
    onExpandCollapse,
    onCreateCardClick,
    onCardClick,
    isInvoiceCountLoading,
    yearKey,
    isPaymentStatLoading,
    billingCards,
    isBillingStatLoading,
    clearingCards,
    collectionCards,
    isClearingStatLoading,
    generalLedgerCards,
    auditTrailCards,
  } = useActivityDashboard();

  return (
    <div className="p-4 md:p-6">
         {/* Header & Search */}
      <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading title="Activity Dashboard" />
        </div>
      </div>

      {isCommunityLoading ||
      isInvoiceCountLoading ||
      isPaymentStatLoading ||
      isBillingStatLoading ||
      isClearingStatLoading ? (
        <Loader />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[20px]">
            <h2 className="text-lg font-[600] items-start flex">
              {communityData?.name}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 col-span-2 justify-end gap-[20px]">
              <Button onClick={onExpandCollapse}>
                {openTabs?.length ? "Collapse All" : "Expand All"}
              </Button>
              <div className="relative">
                <FormLabel>Budget Year</FormLabel>
                <Select
                  showSearch={false}
                  key={yearKey}
                  placeholder=""
                  searchPlaceholder=""
                  noDataPlaceholder=""
                  options={yearOptions(5)?.map((d: any) => ({
                    value: d.value,
                    label: d.label,
                  }))}
                  value={year}
                  onChange={onYearChange}
                  className="h-[36px] w-full"
                />
              </div>
              <div className="relative">
                <FormLabel>Select Community</FormLabel>
                <Select
                  placeholder={""}
                  searchPlaceholder={"Search..."}
                  noDataPlaceholder={"Community No Found"}
                  options={communityList?.map((data: any) => ({
                    value: data?.communityId?.toString(),
                    label: data?.name,
                  }))}
                  value={communityId}
                  onChange={onCommunityChange}
                  className="h-[36px]"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-col-1 gap-[20px] mt-[20px]">
            <Accordion
              type="multiple"
              value={openTabs}
              onValueChange={onAccordionChange}
            >
              {/* Invoice Document Management */}
              <AccordionItem
                value={INVOICE}
                className="bg-white px-5 rounded-lg mb-[20px] shadow-md"
              >
                <AccordionTrigger className="cursor-pointer">
                  Invoice Document Management
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px] py-4">
                    {invoiceCards?.map((data, index) => {
                      return (
                        <ActivityCard
                          key={`invoice-${index}`}
                          isCreate={data.id == "create"}
                          isSummary={data.id == "summary"}
                          hideCount={data.hideCount}
                          data={data}
                          onCardClick={
                            data.id == "create" && data?.link
                              ? onCreateCardClick(data?.link)
                              : onCardClick(data?.link)
                          }
                        />
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
               {/* Payment Document Management */}
              <AccordionItem
                value={PAYMENT}
                className="bg-white px-5 mb-[20px] rounded-lg shadow-md"
              >
                <AccordionTrigger className="cursor-pointer">
                  Payment Document Management
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px] py-4">
                    {paymentCards?.map((data, index) => {
                      return (
                        <ActivityCard
                          key={`payment-${index}`}
                          isCreate={data.id == "create"}
                          isSummary={data.id == "summary"}
                          hideCount={data.hideCount}
                          data={data}
                          onCardClick={
                            data.id == "create" && data?.link
                              ? onCreateCardClick(data?.link)
                              : onCardClick(data?.link)
                          }
                        />
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
               {/* Billing Management */}
              <AccordionItem
                value={BILLING}
                className="bg-white px-5 mb-[20px] rounded-lg shadow-md"
              >
                <AccordionTrigger className="cursor-pointer">
                  Billing Management
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px] py-4">
                    {billingCards?.map((data, index) => {
                      return (
                        <ActivityCard
                          key={`billing-${index}`}
                          isCreate={data.id == "create"}
                          isSummary={data.id == "summary"}
                          isValueObject={data.id == "valueObject"}
                          isTitleOnly={data.id == "titleOnly"}
                          hideCount={(data as any).hideCount || false}
                          data={data}
                          onCardClick={
                            data.id == "create" && data?.link
                              ? onCreateCardClick(data?.link)
                              : onCardClick(data?.link)
                          }
                        />
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
               {/* Collection Management */}
              <AccordionItem
                value={COLLECTION}
                className="bg-white px-5 mb-[20px] rounded-lg shadow-md"
              >
                <AccordionTrigger className="cursor-pointer">
                  Collection
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px] py-4">
                    {collectionCards?.map((data, index) => {
                      return (
                        <ActivityCard
                          key={`collection-${index}`}
                          isCreate={data.id == "create"}
                          isSummary={data.id == "summary"}
                          isValueObject={data.id == "valueObject"}
                          isTitleOnly={data.id == "titleOnly"}
                          hideCount={(data as any).hideCount || false}
                          data={data}
                          onCardClick={
                            data.id == "create" && data?.link
                              ? onCreateCardClick(data?.link)
                              : onCardClick(data?.link)
                          }
                        />
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
                 {/* Clearing Documents */}
              <AccordionItem
                value={CLEARING}
                className="bg-white px-5 mb-[20px] rounded-lg shadow-md"
              >
                <AccordionTrigger className="cursor-pointer">
                  Clearing Documents
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px] py-4">
                    {clearingCards?.map((data, index) => {
                      return (
                        <ActivityCard
                          key={`clearing-${index}`}
                          isCreate={data.id == "create"}
                          isSummary={data.id == "summary"}
                          isValueObject={data.id == "valueObject"}
                          isTitleOnly={data.id == "titleOnly"}
                          hideCount={(data as any).hideCount || false}
                          data={data}
                          onCardClick={
                            data.id == "create" && data?.link
                              ? onCreateCardClick(data?.link)
                              : onCardClick(data?.link)
                          }
                        />
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
              {/*General Ledger*/}

              <AccordionItem
                value={GL}
                className="bg-white px-5 mb-[20px] rounded-lg shadow-md"
              >
                <AccordionTrigger className="cursor-pointer">
                  General Ledger
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px] py-4">
                    {generalLedgerCards?.map((data: any, index: number) => {
                      return (
                        <ActivityCard
                          key={`general-ledger-${index}`}
                          isCreate={data.id == "create"}
                          isSummary={data.id == "summary"}
                          isValueObject={data.id == "valueObject"}
                          isTitleOnly={data.id == "titleOnly"}
                          hideCount={(data as any).hideCount || false}
                          data={data}
                          onCardClick={
                            data.id == "create" && data?.link
                              ? onCreateCardClick(data?.link)
                              : onCardClick(data?.link)
                          }
                        />
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem
                value={AUDIT_TRAIL}
                className="bg-white px-5 mb-[20px] rounded-lg shadow-md"
              >
                <AccordionTrigger className="cursor-pointer">
                  Admin & Audit
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px] py-4">
                    {auditTrailCards?.map((data: any, index: number) => {
                      return (
                        <ActivityCard
                          key={`audit-trail-${index}`}
                          isCreate={data.id == "create"}
                          isSummary={data.id == "summary"}
                          isValueObject={data.id == "valueObject"}
                          isTitleOnly={data.id == "titleOnly"}
                          hideCount={(data as any).hideCount || false}
                          data={data}
                          onCardClick={
                            data.id == "create" && data?.link
                              ? onCreateCardClick(data?.link)
                              : onCardClick(data?.link)
                          }
                        />
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
              <CostAnalytics
                onCreateCardClick={onCreateCardClick}
                onCardClick={onCardClick}
              />
            </Accordion>
          </div>
        </>
      )}
    </div>
  );
};

export default ActivityDashboard;
